import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { useInjectReducer } from 'utils/redux-injectors';
import { GlobalState } from './types';
import { baseQuery, endpoints } from 'utils/api/endpoints';
import { createApi } from '@reduxjs/toolkit/query/react';

export const initialState: GlobalState = {
  user: JSON.parse(localStorage.getItem('user') || 'null') || null,
  // user: null,
  loading: false,
  error: null,
  lineSelection: {
    planLabel: '2',
    linesProgressValue: 25
  },
  email:"",
  isProfileCompleted: JSON.parse(localStorage.getItem('isProfileCompleted') || 'false'),
  ispracticeprofilecompleted: JSON.parse(localStorage.getItem('ispracticeprofilecompleted') || 'false'),
  socketNotification:null,
  isLogin: JSON.parse(localStorage.getItem('isLogin') || 'false'),
  paidCampaignsData:null,
  paidCampaignsStatus:'',
};

const slice = createSlice({
  name: 'global',
  initialState,
  reducers: {
    setUser(state, action: PayloadAction<any>) {
      localStorage.setItem('user', JSON.stringify(action.payload));
      state.user = action.payload;
      // Set profile completion flags based on user data
      if (action.payload) {
        state.isProfileCompleted = action.payload.isProfileCompleted || false;
        state.ispracticeprofilecompleted = action.payload.isPracticeProfileComplete || false;
      }
    },
    clearUser(state) {
      state.user = null;
      localStorage.removeItem('user');
    },
    setPlanLabel(state, action: PayloadAction<string>) {
      state.lineSelection.planLabel = action.payload;
    },
    setLinesProgressValue(state, action: PayloadAction<number>) {
      state.lineSelection.linesProgressValue = action.payload;
    },
    resetLineSelection(state) {
      state.lineSelection = initialState.lineSelection;
    },
    setEmail(state, action: PayloadAction<any>) {
      state.email = action.payload;
      localStorage.setItem('email', JSON.stringify(action.payload));
    },
    setIsProfileCompleted(state, action: PayloadAction<boolean>) {
      state.isProfileCompleted = action.payload;
      localStorage.setItem('isProfileCompleted', JSON.stringify(action.payload));
    },
    setIspracticeprofilecompleted(state, action: PayloadAction<boolean>) {
      state.ispracticeprofilecompleted = action.payload;
      localStorage.setItem('ispracticeprofilecompleted', JSON.stringify(action.payload));
    },
    setSocketNotification(state, action: PayloadAction<any>) {
      state.socketNotification = action.payload;
      localStorage.setItem('socketNotification', JSON.stringify(action.payload));
    },
    clearSocketNotification(state) {
      state.socketNotification = null;
      localStorage.removeItem('socketNotification');
    },
    logout(state) {
      // Clear all user-related state
      state.user = null;
      state.email = "";
      state.isProfileCompleted = false;
      state.ispracticeprofilecompleted = false;
      state.socketNotification = null;
      state.isLogin = false;
      state.lineSelection = initialState.lineSelection;
      
      // Clear all localStorage items
      localStorage.removeItem('user');
      localStorage.removeItem('email');
      localStorage.removeItem('isProfileCompleted');
      localStorage.removeItem('ispracticeprofilecompleted');
      localStorage.removeItem('socketNotification');
      localStorage.removeItem('isLogin');
    },
    setIsLogin(state, action: PayloadAction<boolean>) {
      state.isLogin = action.payload;
      localStorage.setItem('isLogin', JSON.stringify(action.payload));
    },
    setPaidCampaignsData(state, action: PayloadAction<any>) {
      state.paidCampaignsData = action.payload;
      localStorage.setItem('paidCampaignsData', JSON.stringify(action.payload));
    },
    clearPaidCampaignsData(state) {
      state.paidCampaignsData = null;
      localStorage.removeItem('paidCampaignsData');
    },
    setPaidCampaignsStatus(state, action: PayloadAction<string>) {
      state.paidCampaignsStatus = action.payload;
      localStorage.setItem('paidCampaignsStatus', JSON.stringify(action.payload));
    },
    clearPaidCampaignsStatus(state) {
      state.paidCampaignsStatus = '';
      localStorage.removeItem('paidCampaignsStatus');
    },
  }
});

export const selectIsLogin = (state: any) => {
  // If Redux state is available, use it
  if (state.global?.isLogin !== undefined) {
    return state.global.isLogin;
  }
  // Fallback to localStorage if Redux state is missing (e.g., on refresh)
  return JSON.parse(localStorage.getItem('isLogin') || 'false');
};
 export const selectPaidCampaignsStatus = (state: any) =>
  state.global.paidCampaignsStatus;

export const selectEmail = (state: any) =>
  state.global.email;

export const selectIsProfileCompleted = (state: any) =>
  state.global.isProfileCompleted;

export const selectIspracticeprofilecompleted = (state: any) =>
  state.global.ispracticeprofilecompleted;

export const selectSocketNotification = (state: any) =>
  state.global.socketNotification;

export const { actions: globalActions } = slice;

export const api = createApi({
  reducerPath: 'globalApi',
  baseQuery,
  tagTypes: ['Notification','MarkAllRead'],
  endpoints: (builder) => ({
      // Get notification list
      notificationList: builder.query<any, { page?: number; limit?: number }>({
        query: (params) => ({
          url: endpoints.notificationList.url,
          method: endpoints.notificationList.method,
          params,
        }),
        transformResponse: (response: any) => {
          if (!response) return { data: [], pagination: null };
          
          // Handle the new API response structure with nested data.documents
          if (response.data?.documents && response.data?.pageNumber !== undefined) {
            return {
              data: response.data.documents,
              pagination: {
                documents: response.data.documents,
                pageNumber: response.data.pageNumber,
                pageSize: response.data.pageSize,
                totalDocuments: response.data.totalDocuments,
                totalPages: response.data.totalPages,
                // Legacy support
                hasNextPage: response.data.pageNumber < response.data.totalPages,
                hasPrevPage: response.data.pageNumber > 1,
                limit: response.data.pageSize,
                nextPage: response.data.pageNumber < response.data.totalPages ? response.data.pageNumber + 1 : null,
                page: response.data.pageNumber,
                prevPage: response.data.pageNumber > 1 ? response.data.pageNumber - 1 : null,
                total: response.data.totalDocuments,
              }
            };
          }
          
          // Handle the flat structure (documents at root level)
          if (response.documents && response.pageNumber !== undefined) {
            return {
              data: response.documents,
              pagination: {
                documents: response.documents,
                pageNumber: response.pageNumber,
                pageSize: response.pageSize,
                totalDocuments: response.totalDocuments,
                totalPages: response.totalPages,
                // Legacy support
                hasNextPage: response.pageNumber < response.totalPages,
                hasPrevPage: response.pageNumber > 1,
                limit: response.pageSize,
                nextPage: response.pageNumber < response.totalPages ? response.pageNumber + 1 : null,
                page: response.pageNumber,
                prevPage: response.pageNumber > 1 ? response.pageNumber - 1 : null,
                total: response.totalDocuments,
              }
            };
          }
          
          // Fallback for old structure
          return response || { data: [], pagination: null };
        },
        providesTags: ['Notification','MarkAllRead'],
      }),
      // Mark notification as read
      markNotificationAsRead: builder.mutation<any, { notificationId: string }>({
        query: ({ notificationId }) => ({
          url: endpoints.notificationMarkAsRead.url.replace(':notificationId', notificationId),
          method: endpoints.notificationMarkAsRead.method,
        }),
        transformResponse: (response: any) => response || [],
        transformErrorResponse: (response: any) => response || [],
        invalidatesTags: ['Notification'],

      }),
      // Delete notification
      deleteNotification: builder.mutation<any, { notificationId: string }>({
        query: ({ notificationId }) => ({
          url: endpoints.notificationDelete.url.replace(':notificationId', notificationId),
          method: endpoints.notificationDelete.method,
        }),
        transformResponse: (response: any) => response || [],
        transformErrorResponse: (response: any) => response || [],
        invalidatesTags: ['Notification','MarkAllRead'],
      }),
      // Mark all notifications as read
      markAllNotificationsAsRead: builder.mutation<any, { notificationIds: string[] }>({
        query: ({ notificationIds }) => ({
          url: endpoints.notificationMarkAllAsRead.url,
          method: endpoints.notificationMarkAllAsRead.method,
          body: { notificationIds },
        }),
        transformResponse: (response: any) => response || [],
        transformErrorResponse: (response: any) => response || [],
        invalidatesTags: ['Notification', 'MarkAllRead'],
      }),
    }),
  })


export const useGlobalSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectReducer({ key: api.reducerPath, reducer: api.reducer });
  return { 
    actions: slice.actions,
    ...api,
  };
};

export const useGlobalApi = () => {
  useInjectReducer({ key: api.reducerPath, reducer: api.reducer });
  return api;
};

export const { 
  useNotificationListQuery,
  useMarkNotificationAsReadMutation,
  useDeleteNotificationMutation,
  useMarkAllNotificationsAsReadMutation 
} = api;

/**
 * Example Usage:
 *
 * export function MyComponentNeedingThisSlice() {
 *  const { actions } = useGlobalSlice();
 *
 *  const onButtonClick = (evt) => {
 *    dispatch(actions.someAction());
 *   };
 * }
 */
